import { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { validateRequired, ErrorMessage } from "../../utlis/function.js";
import { callApi } from "../../services/ApiServices";
import CommonLoader from "../common/CommonLoader.jsx";

const selectStyles = (hasError) => ({
  control: (base) => ({
    ...base,
    minHeight: "48px",
    borderRadius: "9999px",
    borderColor: hasError ? "#ef4444" : "#DADADA",
    boxShadow: "none",
    paddingLeft: "8px",
    "&:hover": { borderColor: hasError ? "#ef4444" : "#DADADA" },
  }),
  placeholder: (base) => ({ ...base, color: "#9ca3af" }),
  menu: (base) => ({ ...base, borderRadius: "10px", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#f3f4f6" : "#fff",
    color: "#111827",
    cursor: "pointer",
  }),
});

export const productOptions = [
  { value: "15sec", label: "15Sec Ad" },
  { value: "30sec", label: "30Sec Ad" },
  { value: "weather", label: "Weather" },
  { value: "sports", label: "Sports" },
  { value: "finance", label: "Finance" },
  { value: "designFee", label: "Design Fee" },
];

export const discountTypeOptions = [
  { value: "fixed", label: "Fixed Amount ($)" },
  { value: "percentage", label: "Percentage (%)" },
];

const AddCouponModal = ({
  onClose,
  title = "Add Coupon",
  description = "Create a new coupon code.",
  onSave,
  isLoading,
  initialValues,
}) => {
  const [formData, setFormData] = useState(() => ({
    code: "",
    validFrom: "",
    validTo: "",
    clubs: [],
    user: null,
    product: null,
    usesCount: "",
    discountType: discountTypeOptions[0],
    discount: "",
    ...initialValues,
  }));
  const [errors, setErrors] = useState({});
  const [clubOptions, setClubOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const searchTimeout = useRef(null);

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    getClubNames();
    return () => clearTimeout(searchTimeout.current);
  }, []);

  const getClubNames = async () => {
    try {
      const response = await callApi({
        method: "get",
        url: "admin/club-name",
      });
      const options = response?.data?.map((club) => ({
        value: club?.id ?? club?.clubName,
        label: club?.clubName,
      }));
      setClubOptions(options || []);
    } catch (error) {
      console.error("Error fetching club Names:", error);
    }
  };

  const searchUsers = async (search) => {
    setUserSearchLoading(true);
    try {
      const response = await callApi({
        method: "get",
        url: `users/advertisers?page=1&limit=20&search=${encodeURIComponent(search)}`,
      });
      const options = response?.data?.map((user) => ({
        value: user?.id,
        label: user?.email ? `${user?.name || "-"} (${user.email})` : user?.name,
      }));
      setUserOptions(options || []);
    } catch (error) {
      console.error("Error searching users:", error);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleUserInputChange = (value, { action }) => {
    if (action !== "input-change") return;
    clearTimeout(searchTimeout.current);
    const trimmed = value.trim();
    if (!trimmed) {
      setUserOptions([]);
      return;
    }
    searchTimeout.current = setTimeout(() => searchUsers(trimmed), 400);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateFields = () => {
    const newErrors = {};

    if (!validateRequired(formData.code)) {
      newErrors.code = "Code is required";
    } else if (/\s/.test(formData.code)) {
      newErrors.code = "Code cannot contain spaces";
    }

    if (!validateRequired(formData.validFrom)) {
      newErrors.validFrom = "Valid from date is required";
    }

    if (formData.validTo && formData.validFrom && formData.validTo < formData.validFrom) {
      newErrors.validTo = "Valid to date should be after valid from date";
    }

    if (!validateRequired(String(formData.usesCount))) {
      newErrors.usesCount = "Uses count is required";
    } else if (!Number.isInteger(Number(formData.usesCount)) || Number(formData.usesCount) < 1) {
      newErrors.usesCount = "Uses count must be a whole number of at least 1";
    }

    const discountValue = Number(formData.discountValue);
    if (!formData.discountType) {
      newErrors.discountType = "Discount type is required";
    }
    if (!validateRequired(String(formData.discountValue))) {
      newErrors.discountValue = "Discount value is required";
    } else if (isNaN(discountValue) || discountValue <= 0) {
      newErrors.discountValue = "Discount value must be greater than 0";
    } else if (formData.discountType?.value === "percentage" && discountValue > 100) {
      newErrors.discountValue = "Percentage cannot be more than 100";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateFields()) return;
    const success = await onSave(formData);
    if (success !== false) onClose();
  };

  const inputClass = (field) =>
    `w-full h-[48px] px-4 rounded-full border focus:outline-none transition-colors ${
      errors[field] ? "border-red-500" : "border-[#DADADA]"
    }`;

  return (
    <div className="fixed inset-0 bg-[#000000b5] flex items-center justify-center z-50">
      <div className="bg-white rounded-[20px] max-w-[811px] w-full mx-4 relative shadow-[0_4px_31.9px_11px_rgba(255,255,255,0.25)] animate-fade-in">
        {isLoading && <CommonLoader fullScreen />}

        <div className="p-8">
          <div className="max-h-[80vh] overflow-y-auto">
            <h2 className="text-[28px] font-semibold text-black mb-2">{title}</h2>
            <p className="text-[16px] text-[#666666] mb-6">{description}</p>

            <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
              <div className="grid md:grid-cols-2 grid-cols-1 gap-3">
                {/* Code */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-black mb-2">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter coupon code"
                    value={formData.code}
                    onChange={(e) => handleInputChange("code", e.target.value.toUpperCase())}
                    className={inputClass("code")}
                  />
                  <ErrorMessage message={errors.code} />
                </div>

                {/* Uses Count */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-black mb-2">
                    Uses Count <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    placeholder="Enter number of uses"
                    value={formData.usesCount}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
                    }}
                    onChange={(e) => handleInputChange("usesCount", e.target.value)}
                    className={inputClass("usesCount")}
                  />
                  <ErrorMessage message={errors.usesCount} />
                </div>

                {/* Discount Type */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-black mb-2">
                    Discount Type <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={discountTypeOptions}
                    value={formData.discountType}
                    onChange={(option) => handleInputChange("discountType", option)}
                    styles={selectStyles(errors.discountType)}
                    placeholder="Select discount type"
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    isSearchable={false}
                  />
                  <ErrorMessage message={errors.discountType} />
                </div>

                {/* Discount Value */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-black mb-2">
                    {formData.discountType?.value === "percentage" ? "Discount (%)" : "Discount Amount ($)"}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={formData.discountType?.value === "percentage" ? "Enter percentage" : "Enter amount"}
                    value={formData.discountValue}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                    }}
                    onChange={(e) => handleInputChange("discountValue", e.target.value)}
                    className={inputClass("discountValue")}
                  />
                  <ErrorMessage message={errors.discountValue} />
                </div>

                {/* Valid From */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-black mb-2">
                    Valid From <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    min={initialValues ? undefined : today}
                    value={formData.validFrom}
                    onChange={(e) => handleInputChange("validFrom", e.target.value)}
                    className={inputClass("validFrom")}
                  />
                  <ErrorMessage message={errors.validFrom} />
                </div>

                {/* Valid To */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-black mb-2">
                    Valid To
                  </label>
                  <input
                    type="date"
                    min={formData.validFrom || (initialValues ? undefined : today)}
                    value={formData.validTo}
                    onChange={(e) => handleInputChange("validTo", e.target.value)}
                    className={inputClass("validTo")}
                  />
                  <ErrorMessage message={errors.validTo} />
                </div>

                {/* Club */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-black mb-2">Clubs</label>
                  <Select
                    isMulti
                    options={clubOptions}
                    value={formData.clubs}
                    onChange={(options) => handleInputChange("clubs", options || [])}
                    styles={selectStyles(errors.clubs)}
                    placeholder="Select clubs"
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    closeMenuOnSelect={false}
                    isClearable
                  />
                  <ErrorMessage message={errors.clubs} />
                </div>

                {/* User */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-black mb-2">User</label>
                  <Select
                    options={userOptions}
                    value={formData.user}
                    onChange={(option) => handleInputChange("user", option)}
                    onInputChange={handleUserInputChange}
                    isLoading={userSearchLoading}
                    filterOption={null}
                    styles={selectStyles(errors.user)}
                    placeholder="Search user by name or email"
                    noOptionsMessage={({ inputValue }) =>
                      inputValue ? "No users found" : "Type to search users"
                    }
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    isClearable
                  />
                  <ErrorMessage message={errors.user} />
                </div>

                {/* Product */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-black mb-2">Product</label>
                  <Select
                    options={productOptions}
                    value={formData.product}
                    onChange={(option) => handleInputChange("product", option)}
                    styles={selectStyles(errors.product)}
                    placeholder="Select product"
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    isClearable
                    isSearchable={false}
                  />
                  <ErrorMessage message={errors.product} />
                </div>
              </div>
            </form>
          </div>

          {/* Buttons */}
          <div className="flex gap-6 justify-center mt-3">
            <button
              onClick={onClose}
              className="w-[255px] h-[52px] rounded-full border border-[#06323A] text-[#06323A] font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="w-[255px] h-[52px] rounded-full bg-[#0A3A44] text-white font-medium hover:opacity-90"
            >
              {isLoading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCouponModal;
